import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
    parseCartItem,
    parseCartItems,
    parseCartMessage, parseSimpleOptions,
} from "../../types/cart/parse";

const selectCartState = (state) => state.cart;

const selectLoading = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return false;
        return !!item.get("loading");
    }
);

const selectMessage = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return null;
        return parseCartMessage(item.get("message"));
    }
);

const selectCartItems = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return [];
        return parseCartItems(item.get("cartItems"));
    }
);

const selectLastAddedItem = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return null;
        return parseCartItem(item.get("lastAddedItem"));
    }
);

const selectPendingNotificationItem = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return null;
        return parseCartItem(item.get("pendingNotificationItem"));
    }
);

const selectRawCount = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return 0;
        return Number(item.get("count")) || 0;
    }
);

const selectVat = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return 0;
        return Number(item.get("vat")) || 0;
    }
);

const selectSum = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return 0;
        return Number(item.get("sum")) || 0;
    }
);
const selectTotal = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return 0;
        return Number(item.get("total")) || 0;
    }
);

const selectShouldRefreshCart = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return false;
        return !!item.get("shouldRefreshCart");
    }
);

const selectCount = createSelector(
    [selectCartItems],
    (items) =>
        items.reduce((total, item) => total + Number(item?.quantity || 0), 0)
);
const selectAddresses = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return [];
        return parseSimpleOptions(item.get("addresses"));
    }
);

const selectCarriers = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return [];
        return parseSimpleOptions(item.get("carriers"));
    }
);

const selectCICarriers = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return [];
        return parseSimpleOptions(item.get("ciCarriers"));
    }
);

const selectCountries = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return [];
        return parseSimpleOptions(item.get("countries"));
    }
);
const selectShouldShowLastAddedNotification = createSelector(
    [selectCartState],
    (item) => {
        if (!item || typeof item.get !== "function") return false;
        return !!item.get("shouldShowLastAddedNotification");
    }
);

const useCartData = () => {
    const loading = useSelector(selectLoading);
    const message = useSelector(selectMessage);
    const cartItems = useSelector(selectCartItems);
    const lastAddedItem = useSelector(selectLastAddedItem);
    const pendingNotificationItem = useSelector(selectPendingNotificationItem);
    const rawCount = useSelector(selectRawCount);
    const count = useSelector(selectCount);
    const vat = useSelector(selectVat);
    const sum = useSelector(selectSum);
    const total = useSelector(selectTotal);
    const shouldRefreshCart = useSelector(selectShouldRefreshCart);
    const addresses = useSelector(selectAddresses);
    const carriers = useSelector(selectCarriers);
    const ciCarriers = useSelector(selectCICarriers);
    const countries = useSelector(selectCountries);
    const shouldShowLastAddedNotification = useSelector(selectShouldShowLastAddedNotification);
    return {
        loading,
        message,
        cartItems,
        lastAddedItem,
        pendingNotificationItem,
        rawCount,
        count,
        vat,
        sum,
        total,
        shouldRefreshCart,
        addresses,
        carriers,
        ciCarriers,
        countries,
        shouldShowLastAddedNotification
    };
};

export default useCartData;